// Read-only access to the book: the index (chapters, lessons, glossary,
// people, prologue) loads at startup; each chapter's verses load on demand.
import { TOTAL_VERSES } from '../../shared/progress.js';

const boot = (typeof window !== 'undefined' && window.__BG__) || { data: {} };

let index = null;
const chapterCache = new Map();
const pending = new Map();

export class StaleBuildError extends Error {}

async function fetchJson(url) {
  let res;
  try {
    res = await fetch(url, { credentials: 'same-origin' });
  } catch (e) {
    throw new Error('offline');
  }
  if (res.status === 404) throw new StaleBuildError('stale');
  if (!res.ok) throw new Error(`http ${res.status}`);
  const type = res.headers.get('content-type') || '';
  if (!type.includes('json')) throw new StaleBuildError('stale');
  return res.json();
}

export async function loadIndex() {
  if (index) return index;
  index = await fetchJson(boot.data.index);
  // Derived lookups.
  index.lessonList = [];
  index.lessonMap = new Map();
  index.chapterMap = new Map();
  // The journey is the 18 chapters, lesson by lesson. The four background
  // lessons (chapter 0) are optional reading: reachable and searchable, but
  // not part of the journey's order or its progress.
  const add = (chapter, journey) => {
    index.chapterMap.set(chapter.ch, chapter);
    chapter.lessons.forEach((l, i) => {
      l.ch = chapter.ch;
      l.index = journey ? index.lessonList.length : -1;
      l.isLast = i === chapter.lessons.length - 1;
      l.isFirst = i === 0;
      if (journey) index.lessonList.push(l);
      index.lessonMap.set(l.id, l);
    });
  };
  add(index.prologue, false);
  index.chapters.forEach((c) => add(c, true));
  index.glossaryMap = new Map(index.glossary.map((g) => [g.id, g]));
  index.peopleMap = new Map(index.people.map((p) => [p.id, p]));
  return index;
}

export function getIndex() {
  return index;
}

export function chapter(ch) {
  return index.chapterMap.get(Number(ch)) || null;
}

export function lesson(id) {
  return index.lessonMap.get(id) || null;
}

export function lessons() {
  return index.lessonList;
}

export function concept(id) {
  return index.glossaryMap.get(id) || null;
}

export function person(id) {
  return index.peopleMap.get(id) || null;
}

export function verseCount(ch) {
  return chapter(ch)?.verses || 0;
}

export async function loadChapter(ch) {
  const n = Number(ch);
  if (chapterCache.has(n)) return chapterCache.get(n);
  if (pending.has(n)) return pending.get(n);
  const url = boot.data.chapters[n];
  const p = fetchJson(url)
    .then((data) => {
      data.verseMap = new Map(data.verses.map((v) => [v.v, v]));
      data.lessonExtras = new Map(data.lessons.map((l) => [l.id, l]));
      chapterCache.set(n, data);
      pending.delete(n);
      return data;
    })
    .catch((e) => {
      pending.delete(n);
      throw e;
    });
  pending.set(n, p);
  return p;
}

export function cachedChapter(ch) {
  return chapterCache.get(Number(ch)) || null;
}

export async function loadVerse(id) {
  const [ch, v] = String(id).split('.').map(Number);
  const data = await loadChapter(ch);
  return data.verseMap.get(v) || null;
}

export async function loadAllChapters() {
  return Promise.all(index.chapters.map((c) => loadChapter(c.ch)));
}

export function verseIdsForLesson(l) {
  if (!l || l.ch === 0) return [];
  const ids = [];
  for (let v = l.from; v <= l.to; v++) ids.push(`${l.ch}.${v}`);
  return ids;
}

export function lessonForVerse(ch, v) {
  const c = chapter(ch);
  if (!c) return null;
  return c.lessons.find((l) => v >= l.from && v <= l.to) || null;
}

// ---------------------------------------------------------------------------
// Progress helpers that need the book structure.

export function lessonDone(progress, id) {
  return !!progress.lessons[id];
}

export function chapterStats(progress, ch) {
  const c = ch === 0 ? index.prologue : chapter(ch);
  const total = c.lessons.length;
  const done = c.lessons.filter((l) => progress.lessons[l.id]).length;
  let versesRead = 0;
  if (ch > 0) {
    for (let v = 1; v <= c.verses; v++) if (progress.verses[`${ch}.${v}`]) versesRead++;
  }
  return { total, done, versesRead, complete: done === total };
}

export function overallStats(progress) {
  const total = index.lessonList.length;
  const done = index.lessonList.filter((l) => progress.lessons[l.id]).length;
  const verses = Object.keys(progress.verses).length;
  const chaptersDone = index.chapters.filter((c) => c.lessons.every((l) => progress.lessons[l.id])).length;
  return { total, done, verses, totalVerses: TOTAL_VERSES, chaptersDone, fraction: total ? done / total : 0 };
}

// Where "Continue" should take the learner: the lesson they are in the middle
// of, otherwise the first lesson they have not finished.
export function nextStop(progress) {
  const c = progress.current;
  if (c && index.lessonMap.has(c.lesson) && !progress.lessons[c.lesson]) {
    return { lesson: index.lessonMap.get(c.lesson), step: c.step, resuming: c.step > 0 };
  }
  const next = index.lessonList.find((l) => !progress.lessons[l.id]);
  if (next) return { lesson: next, step: 0, resuming: false };
  return null;
}

// The lesson that follows. After the last background lesson that is the
// learner's next lesson in the chapters (chapter 1 for a newcomer).
export function nextLessonAfter(id, progress = null) {
  const l = lesson(id);
  if (!l) return null;
  if (l.ch === 0) {
    const bg = index.prologue.lessons;
    const following = bg[bg.indexOf(l) + 1];
    if (following) return following;
    return (progress && index.lessonList.find((x) => !progress.lessons[x.id])) || (progress ? null : index.lessonList[0]);
  }
  return index.lessonList[l.index + 1] || null;
}

// Minutes estimate for a lesson: reading plus the quick check.
export function lessonMinutes(l) {
  if (l.ch === 0) return 4;
  const verses = l.to - l.from + 1;
  return Math.max(4, Math.round(verses * 0.9 + 2));
}

// ---------------------------------------------------------------------------
// Search across every verse, concept, person and lesson.

const norm = (s) => String(s || '')
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')
  .toLowerCase();

export async function search(query) {
  const q = norm(query).trim();
  if (q.length < 2) return { verses: [], concepts: [], people: [], lessons: [] };
  const terms = q.split(/\s+/).filter(Boolean);
  const score = (fields) => {
    let s = 0;
    for (const t of terms) {
      let hit = false;
      fields.forEach(([text, w]) => {
        const n = norm(text);
        const i = n.indexOf(t);
        if (i >= 0) {
          hit = true;
          s += w * (i === 0 || /\W/.test(n[i - 1]) ? 2 : 1);
        }
      });
      if (!hit) return 0;
    }
    return s;
  };

  const refMatch = /^(\d{1,2})[.:\s](\d{1,2})$/.exec(q);
  const concepts = index.glossary
    .map((g) => ({ item: g, s: score([[g.term, 6], [g.iast, 5], [g.short, 2], [g.body, 1]]) }))
    .filter((x) => x.s > 0).sort((a, b) => b.s - a.s).slice(0, 8).map((x) => x.item);
  const people = index.people
    .map((p) => ({ item: p, s: score([[p.name, 6], [p.role, 2], [p.short, 1], [(p.epithets || []).map((e) => e.name).join(' '), 4]]) }))
    .filter((x) => x.s > 0).sort((a, b) => b.s - a.s).slice(0, 6).map((x) => x.item);
  const lessonsFound = [...index.lessonList, ...index.prologue.lessons]
    .map((l) => ({ item: l, s: score([[l.title, 5], [l.idea, 2], [l.takeaway, 1]]) }))
    .filter((x) => x.s > 0).sort((a, b) => b.s - a.s).slice(0, 6).map((x) => x.item);

  const chapters = await loadAllChapters();
  let verses = [];
  for (const data of chapters) {
    for (const v of data.verses) {
      const id = `${data.ch}.${v.v}`;
      let s = refMatch && Number(refMatch[1]) === data.ch && Number(refMatch[2]) === v.v ? 1000 : 0;
      if (!s) s = score([[v.translation, 3], [v.meaning, 1], [v.iast.join(' '), 2]]);
      if (s > 0) verses.push({ id, ch: data.ch, v: v.v, verse: v, s });
    }
  }
  verses.sort((a, b) => b.s - a.s || a.ch - b.ch || a.v - b.v);
  verses = verses.slice(0, 40);
  return { verses, concepts, people, lessons: lessonsFound };
}

// ---------------------------------------------------------------------------
// The dawn palette: chapter 1 opens at night, chapter 18 ends in full light.
// Colours are interpolated in OKLab so the journey reads as one sky.

const STOPS = [
  [0, '#0f1433'],
  [0.22, '#262d6a'],
  [0.42, '#5a3f8c'],
  [0.6, '#a2507f'],
  [0.76, '#dc7457'],
  [0.9, '#f0a650'],
  [1, '#f7d488'],
];

function hexToRgb(hex) {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function srgbToLinear(c) {
  c /= 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}
function linearToSrgb(c) {
  const v = c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055;
  return Math.round(Math.min(1, Math.max(0, v)) * 255);
}
function toOklab([r, g, b]) {
  const [lr, lg, lb] = [r, g, b].map(srgbToLinear);
  const l = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb);
  const m = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb);
  const s = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb);
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ];
}
function fromOklab([L, a, b]) {
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
  return [
    linearToSrgb(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s),
    linearToSrgb(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s),
    linearToSrgb(-0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s),
  ];
}
const toHex = (rgb) => `#${rgb.map((x) => x.toString(16).padStart(2, '0')).join('')}`;

export function skyAt(f) {
  const x = Math.min(1, Math.max(0, f));
  let i = 0;
  while (i < STOPS.length - 2 && x > STOPS[i + 1][0]) i++;
  const [a, ca] = STOPS[i];
  const [b, cb] = STOPS[i + 1];
  const t = (x - a) / (b - a);
  const A = toOklab(hexToRgb(ca));
  const B = toOklab(hexToRgb(cb));
  return toHex(fromOklab(A.map((v, k) => v + (B[k] - v) * t)));
}

function luminance(hex) {
  const [r, g, b] = hexToRgb(hex).map(srgbToLinear);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

// Text colour that stays readable on a given sky colour.
export function inkOn(hex) {
  return luminance(hex) > 0.28 ? '#26170a' : '#fff8ec';
}

export function chapterSky(ch) {
  const f = ch <= 0 ? 0 : (ch - 1) / 17;
  const top = skyAt(f);
  const bottom = skyAt(Math.min(1, f + 0.07));
  return { top, bottom, ink: inkOn(top), f };
}
